// pages/superadmin/QAQueue.jsx
// Phase 4, QA Workflow. Sites land here (qaStatus="pending_qa") right
// after dudaPollerJob finishes injecting content. Nothing here is
// customer-visible or emailed until a reviewer approves it.
import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import apiService from "../../services/api";
import NotificationBell from "../../components/NotificationBell";
import { useAuth } from "../../contexts/AuthContext";
import { Dialog, DialogContent } from "../../arc/components/dialog/dialog";
import { SearchField } from "../../arc/components/search-field/search-field";
import { Textarea } from "../../arc/components/textarea/textarea";
import { Input } from "../../arc/components/input/input";
import { Skeleton } from "../../arc/components/skeleton/skeleton";
import { ThemeSwitch } from "../../arc/components/theme-switch/theme-switch";
import { applyThemeWithTransition } from "../../utils/themeTransition";

const styles = `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  .dash-root.dark .dash-header { box-shadow: 0 1px 0 rgba(255,255,255,0.03), 0 2px 20px rgba(0,0,0,0.6); }

  .dash-root { min-height: 100vh; background: var(--bg); color: var(--text); font-family: var(--sans); font-size: 14px; line-height: 1.5; -webkit-font-smoothing: antialiased; }

  /* HEADER, matches CustomersManagement header */
  .dash-header { background: #082a58; border-bottom: 1px solid rgba(255,255,255,0.07);  position: sticky; top: 0; z-index: 100; }
  .dash-header-inner { max-width: 1560px; margin: 0 auto; padding: 0 28px; height: 64px; display: flex; align-items: center; justify-content: space-between; gap: 16px; }
  .dash-brand { display: flex; align-items: center; gap: 12px; flex-shrink: 0; }
  
  .btn-back { display: inline-flex; align-items: center; justify-content: center; width: 32px; height: 32px; background: rgba(255,255,255,0.07); border: 1px solid rgba(255,255,255,0.12); border-radius: var(--radius-sm); color: rgba(255,255,255,0.85); transition: all var(--t); margin-right: 4px; }
  .btn-back:hover { background: rgba(255,255,255,0.13); border-color: rgba(255,255,255,0.22); color: #fff; transform: translateX(-2px); }

  .dash-brand-text { display: flex; flex-direction: column; gap: 1px; }
  .dash-brand-name { font-size: 13.5px; font-weight: 600; color: #fff; letter-spacing: -0.01em; }
  .dash-brand-sub { font-family: var(--mono); font-size: 9px; letter-spacing: 0.1em; text-transform: uppercase; color: rgba(255,255,255,0.38); }
  .dash-header-right { display: flex; align-items: center; gap: 8px; }

  .btn-header { display: inline-flex; align-items: center; justify-content: center; gap: 6px; min-height: 36px; padding: 0 14px; background: rgba(255,255,255,0.07); border: 1px solid rgba(255,255,255,0.12); border-radius: 8px; font-family: var(--sans); font-size: 12.5px; font-weight: 500; color: rgba(255,255,255,0.78); cursor: pointer; text-decoration: none; transition: all var(--t); }
  .btn-header:hover { background: rgba(255,255,255,0.13); border-color: rgba(255,255,255,0.22); color: #fff; }

  .user-pill { display: flex; align-items: center; gap: 7px; padding: 5px 12px; background: rgba(255,255,255,0.07); border: 1px solid rgba(255,255,255,0.12); border-radius: 100px; font-size: 12.5px; font-weight: 500; color: rgba(255,255,255,0.85); }
  .online-dot { width: 6px; height: 6px; border-radius: 50%; background: #34d399; box-shadow: 0 0 0 2px rgba(52,211,153,0.25); flex-shrink: 0; }

  /* SETTINGS */
  .settings-wrap { position: relative; }
  .settings-panel { display: none; position: absolute; right: 0; top: calc(100% + 8px); min-width: 210px; background: #0d2d5a; border: 1px solid rgba(255,255,255,0.1); border-radius: var(--radius); box-shadow: 0 12px 40px rgba(0,0,0,0.5); z-index: 200; padding: 4px; }
  .settings-panel.open { display: block; }
  .dash-root.dark .settings-panel { background: #1a1a24; border-color: var(--border); }
  .settings-row { display: flex; align-items: center; justify-content: space-between; padding: 9px 12px; font-size: 12.5px; color: rgba(255,255,255,0.7); border-radius: var(--radius-sm); cursor: pointer; transition: background var(--t); }
  .settings-row:hover { background: rgba(255,255,255,0.07); color: #fff; }

  /* TOGGLE */
  .toggle { position: relative; width: 32px; height: 18px; flex-shrink: 0; }
  .toggle input { opacity: 0; width: 0; height: 0; position: absolute; }
  .toggle-track { position: absolute; inset: 0; background: rgba(255,255,255,0.2); border-radius: 100px; cursor: pointer; transition: background var(--t); }
  .toggle-track::after { content: ''; position: absolute; width: 14px; height: 14px; left: 2px; top: 2px; background: #fff; border-radius: 50%; box-shadow: 0 1px 4px rgba(0,0,0,0.25); transition: transform 0.2s var(--ease); }
  .toggle input:checked ~ .toggle-track { background: #34d399; }
  .toggle input:checked ~ .toggle-track::after { transform: translateX(14px); }

  /* MAIN */
  .dash-main { max-width: 1560px; margin: 0 auto; padding: 28px; display: flex; flex-direction: column; gap: 20px; }

  /* SEARCH */
  .search-wrap { position: relative; flex: 1; max-width: 400px; }
  .search-input { width: 100%; padding: 8px 14px 8px 36px; background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius-sm); color: var(--text); font-family: var(--sans); font-size: 13px; outline: none; transition: border-color var(--t), box-shadow var(--t); }
  .search-input::placeholder { color: var(--text-faint); }
  .search-input:focus { border-color: var(--accent-mid); box-shadow: 0 0 0 3px var(--accent-dim); }
  .search-icon { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); color: var(--text-faint); pointer-events: none; }
  .search-clear { position: absolute; right: 8px; top: 50%; transform: translateY(-50%); background: none; border: none; color: var(--text-faint); cursor: pointer; padding: 4px; display: flex; align-items: center; justify-content: center; border-radius: 4px; transition: all var(--t); }
  .search-clear:hover { color: var(--text-muted); background: var(--surface-hover); }

  /* BUTTONS */
  .btn { display: inline-flex; align-items: center; justify-content: center; gap: 8px; min-height: 36px; padding: 0 16px; border: 1px solid transparent; border-radius: 8px; font-family: var(--sans); font-size: 12.5px; font-weight: 500; line-height: 1.45; cursor: pointer; white-space: nowrap; text-decoration: none; outline: none; -webkit-tap-highlight-color: transparent; transition: color var(--t), background var(--t), border-color var(--t), box-shadow var(--t), opacity var(--t); }
  .btn:focus-visible { box-shadow: 0 0 0 3px var(--accent-dim); }
  .btn:disabled { opacity: 0.52; cursor: not-allowed; }
  .btn-sm { min-height: 32px; padding: 0 12px; }
  .btn-md { min-height: 36px; padding: 0 16px; }
  .btn-lg { min-height: 40px; padding: 0 20px; }
  .btn-ghost { background: transparent; border: 1px solid var(--border); color: var(--text-muted); }
  .btn-ghost:hover { background: var(--surface-raised); border-color: var(--border-strong); color: var(--text); }
  .btn-success-ghost { background: var(--green-dim); border: 1px solid var(--green-border); color: var(--green); }
  .btn-success-ghost:hover { background: var(--green); color: #fff; }
  .btn-success-ghost:disabled { opacity: 0.5; cursor: not-allowed; background: var(--green-dim); color: var(--green); }
  .btn-danger-ghost { background: var(--red-dim); border: 1px solid var(--red-border); color: var(--red); }
  .btn-danger-ghost:hover { background: var(--red); color: #fff; }
  .btn-danger-ghost:disabled { opacity: 0.5; cursor: not-allowed; }

  /* TABS */
  .qa-tabs { display: flex; gap: 6px; flex-wrap: wrap; }
  .qa-tab { padding: 6px 14px; border-radius: var(--radius-sm); border: 1px solid var(--border); background: var(--surface); font-family: var(--sans); font-size: 12.5px; font-weight: 500; cursor: pointer; color: var(--text-muted); transition: all var(--t); }
  .qa-tab:hover { border-color: var(--border-strong); color: var(--text); }
  .qa-tab.active { background: var(--accent); color: #fff; border-color: var(--accent); }

  /* PANEL / LIST */
  .table-wrap { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius-lg); box-shadow: var(--shadow-sm); overflow: hidden; }
  .qa-list { display: flex; flex-direction: column; }
  .qa-card { padding: 16px 18px; display: flex; align-items: center; justify-content: space-between; gap: 16px; border-bottom: 1px solid var(--border); transition: background-color 0.12s ease; }
  .qa-card:last-child { border-bottom: none; }
  .qa-card:hover { background-color: var(--surface-hover); }
  .qa-card-info { min-width: 0; flex: 1; }
  .qa-card-name { font-size: 13.5px; font-weight: 600; color: var(--text); letter-spacing: -0.02em; display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
  .qa-card-company-id { font-family: var(--mono); font-size: 11px; font-weight: 400; color: var(--text-faint); background: var(--surface-sub); padding: 1px 8px; border-radius: var(--radius-xs); border: 1px solid var(--border); }
  .qa-card-sub { font-family: var(--mono); font-size: 11px; color: var(--text-faint); margin-top: 3px; }
  .qa-card-actions { display: flex; gap: 8px; flex-shrink: 0; flex-wrap: wrap; }
  .qa-notes-box { margin-top: 8px; padding: 8px 12px; background: var(--amber-dim); border: 1px solid var(--amber-border); border-radius: var(--radius-sm); font-size: 12px; color: var(--amber); }

  /* TABLE TOOLBAR */
  .table-toolbar { display: flex; align-items: center; justify-content: space-between; padding: 8px 14px; border-bottom: 1px solid var(--border); background: var(--surface-raised); gap: 10px; flex-wrap: wrap; }
  .toolbar-label { font-family: var(--mono); font-size: 10.5px; color: var(--text-faint); letter-spacing: 0.04em; }

  /* PAGINATION */
  .pagination { display: flex; align-items: center; justify-content: space-between; padding: 10px 14px; border-top: 1px solid var(--border); background: var(--surface-raised); gap: 10px; flex-wrap: wrap; }
  .page-info { font-family: var(--mono); font-size: 10.5px; color: var(--text-faint); letter-spacing: 0.04em; }
  .page-info b { color: var(--text-muted); font-weight: 500; }
  .page-controls { display: flex; align-items: center; gap: 2px; flex-wrap: wrap; }
  .btn-page { display: flex; align-items: center; justify-content: center; min-width: 28px; height: 28px; padding: 0 7px; background: transparent; border: 1px solid var(--border); color: var(--text-muted); border-radius: var(--radius-sm); font-family: var(--mono); font-size: 11px; cursor: pointer; transition: all var(--transition); }
  .btn-page:hover:not(:disabled) { border-color: var(--accent-mid); color: var(--accent-mid); background: var(--accent-dim); }
  .btn-page:disabled { opacity: 0.25; cursor: not-allowed; }
  .btn-page.active { background: var(--accent); border-color: var(--accent); color: #fff; font-weight: 600; }
  .btn-page.active:hover { background: var(--accent-hover); }
  .page-ellipsis { display: inline-flex; align-items: center; justify-content: center; width: 22px; color: var(--text-faint); font-size: 11px; }
  .page-jump { display: flex; align-items: center; gap: 6px; font-family: var(--mono); font-size: 10.5px; color: var(--text-faint); }
  .page-jump-input { width: 44px; height: 28px; padding: 0 7px; background: var(--bg); border: 1px solid var(--border); border-radius: var(--radius-sm); color: var(--text); font-family: var(--mono); font-size: 10.5px; text-align: center; outline: none; transition: border-color var(--transition), box-shadow var(--transition); }
  .page-jump-input:focus { border-color: var(--accent-mid); box-shadow: 0 0 0 2px var(--accent-dim); }

  /* EMPTY / LOADING */
  .empty-state { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 72px 20px; gap: 12px; text-align: center; }
  .empty-state-label { font-family: var(--mono); font-size: 11px; letter-spacing: 0.05em; text-transform: uppercase; color: var(--text-faint); }
  .spinner { width: 28px; height: 28px; border: 2px solid var(--border); border-top-color: var(--accent-mid); border-radius: 50%; animation: spin 0.7s linear infinite; }
  @keyframes spin { to { transform: rotate(360deg); } }

  /* MODAL */
  .qa-modal-backdrop { position: fixed; inset: 0; background: rgba(0,0,0,0.4); z-index: 400; display: flex; align-items: center; justify-content: center; padding: 20px; }
  .qa-modal { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius-lg); padding: 28px; max-width: 420px; width: 100%; box-shadow: var(--shadow-modal); }
  .qa-modal-title { font-size: 16px; font-weight: 600; color: var(--text); margin-bottom: 6px; letter-spacing: -0.02em; }
  .qa-modal-sub { font-size: 13px; color: var(--text-muted); line-height: 1.6; margin-bottom: 16px; }
  .regen-modal textarea { width: 100%; min-height: 90px; border: 1px solid var(--border); border-radius: var(--radius-sm); padding: 10px; font-family: var(--sans); font-size: 13px; color: var(--text); background: var(--bg); resize: vertical; outline: none; transition: border-color var(--t); }
  .regen-modal textarea:focus { border-color: var(--accent-mid); }
  .qa-modal-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 16px; }

  /* RESPONSIVE */
  @media (max-width: 768px) {
    .dash-header-inner { padding: 0 16px; }
    .dash-main { padding: 16px; }
    .qa-card { flex-direction: column; align-items: flex-start; gap: 12px; }
    .qa-card-actions { width: 100%; justify-content: flex-start; }
    .pagination { flex-direction: column; align-items: stretch; }
    .pagination .page-controls { justify-content: center; }
    .search-wrap { max-width: 100%; }
    .qa-tabs { width: 100%; }
  }
`;

const TABS = [
  { key: "pending_qa", label: "Pending Review" },
  { key: "approved", label: "Approved" },
  { key: "regeneration_requested", label: "Regenerating" },
];

const PAGE_SIZE = 10;

export default function QAQueue() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("pending_qa");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [regenTarget, setRegenTarget] = useState(null);
  const [notes, setNotes] = useState("");
  const [error, setError] = useState(null);
  const [darkMode, setDarkMode] = useState(
    () => localStorage.getItem("axo_theme") === "dark",
  );
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchDebounce, setSearchDebounce] = useState("");

  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    total: 0,
  });
  const [jumpPage, setJumpPage] = useState("");

  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    localStorage.setItem("axo_theme", darkMode ? "dark" : "light");
  }, [darkMode]);

  // Close settings panel on outside click
  useEffect(() => {
    if (!settingsOpen) return;
    const handler = () => setSettingsOpen(false);
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [settingsOpen]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.getQaQueue(
        tab,
        pagination.currentPage,
        PAGE_SIZE,
        searchDebounce,
      );

      // Handle response - same format as CustomerDashboard
      let data = [];
      let total = 0;
      let currentPage = 1;
      let totalPages = 1;

      if (res.data && Array.isArray(res.data)) {
        data = res.data;
        total = res.pagination?.total || data.length;
        currentPage = res.pagination?.page || 1;
        totalPages = res.pagination?.totalPages || Math.ceil(total / PAGE_SIZE);
      } else if (Array.isArray(res)) {
        data = res;
        total = res.length;
        totalPages = Math.ceil(total / PAGE_SIZE);
      } else {
        data = [];
        total = 0;
        totalPages = 1;
      }

      setRows(data);
      setPagination({
        currentPage,
        totalPages,
        total,
      });
    } catch (err) {
      console.error("Failed to load QA queue:", err);
      setError(
        err.response?.data?.message || err.message || "Failed to load QA queue",
      );
    } finally {
      setLoading(false);
    }
  }, [tab, pagination.currentPage, searchDebounce]);

  useEffect(() => {
    load();
  }, [load]);

  // Reset to page 1 when tab or search changes
  useEffect(() => {
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, [tab, searchDebounce]);

  // Handle search with debounce
  const handleSearchChange = (value) => {
    setSearchQuery(value);
    clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setSearchDebounce(value.trim());
    }, 500);
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setSearchDebounce("");
    clearTimeout(searchTimeoutRef.current);
  };

  const handleApprove = async (id) => {
    setBusyId(id);
    try {
      await apiService.approveQa(id);
      // Remove from list and update total count
      setRows((prev) => prev.filter((r) => r.id !== id));
      setPagination((prev) => ({
        ...prev,
        total: prev.total - 1,
        totalPages: Math.ceil((prev.total - 1) / PAGE_SIZE),
      }));
      // If current page is now empty and not first page, go to previous page
      if (rows.length === 1 && pagination.currentPage > 1) {
        setPagination((prev) => ({
          ...prev,
          currentPage: prev.currentPage - 1,
        }));
      }
    } catch (err) {
      console.error("Failed to approve:", err);
      setError(
        err.response?.data?.message || err.message || "Failed to approve",
      );
    } finally {
      setBusyId(null);
    }
  };

  const openRegen = (row) => {
    setRegenTarget(row);
    setNotes("");
  };

  const submitRegen = async () => {
    if (!notes.trim() || !regenTarget) return;
    setBusyId(regenTarget.id);
    try {
      await apiService.requestQaRegeneration(regenTarget.id, notes.trim());
      setRows((prev) => prev.filter((r) => r.id !== regenTarget.id));
      setPagination((prev) => ({
        ...prev,
        total: prev.total - 1,
        totalPages: Math.ceil((prev.total - 1) / PAGE_SIZE),
      }));
      if (rows.length === 1 && pagination.currentPage > 1) {
        setPagination((prev) => ({
          ...prev,
          currentPage: prev.currentPage - 1,
        }));
      }
      setRegenTarget(null);
    } catch (err) {
      console.error("Failed to request regeneration:", err);
      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to request regeneration",
      );
    } finally {
      setBusyId(null);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination((prev) => ({ ...prev, currentPage: newPage }));
    }
  };

  const handleJumpPage = () => {
    const page = parseInt(jumpPage, 10);
    if (!isNaN(page) && page >= 1 && page <= pagination.totalPages) {
      handlePageChange(page);
      setJumpPage("");
    }
  };

  // Generate page numbers for pagination (same as CustomerDashboard)
  const getPageNumbers = () => {
    const { currentPage: c, totalPages: t } = pagination;
    if (t <= 7) return Array.from({ length: t }, (_, i) => i + 1);
    if (c <= 4) return [1, 2, 3, 4, 5, "...", t];
    if (c >= t - 3) return [1, "...", t - 4, t - 3, t - 2, t - 1, t];
    return [1, "...", c - 1, c, c + 1, "...", t];
  };

  // Format company display name with ID
  const formatCompanyDisplay = (row) => {
    const name =
      row.companyName ||
      row.company_name ||
      row.name ||
      row.businessName ||
      row.company?.name ||
      row.company?.companyName ||
      "Unnamed Company";
    const id =
      row.companyId ||
      row.company_id ||
      row.company?.id ||
      row.company?.companyId ||
      row.id ||
      "N/A";
    return { name, id };
  };

  return (
    <div className={`dash-root${darkMode ? " dark" : ""}`}>
      <style>{styles}</style>

      {/* HEADER, matches CustomersManagement header with back button */}
      <header className="dash-header">
        <div className="dash-header-inner">
          <div className="dash-brand">
            <Link
              to="/super-admin"
              className="btn-back"
              title="Back to Dashboard"
            >
              <svg
                width="18"
                height="18"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </Link>
            <div className="dash-brand-text">
              <div className="dash-brand-name">QA Queue</div>
              <div className="dash-brand-sub">Axo Signal</div>
            </div>
          </div>

          <div className="dash-header-right">
            <NotificationBell />
            <div className="settings-wrap" onClick={(e) => e.stopPropagation()}>
              <button
                className="btn-header"
                onClick={() => setSettingsOpen((o) => !o)}
              >
                <svg
                  width="14"
                  height="14"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
                Settings
              </button>
              <div className={`settings-panel${settingsOpen ? " open" : ""}`}>
                <div className="settings-row">
                  <span>Dark mode</span>
                  <ThemeSwitch
                    iconOnly
                    theme={darkMode ? "dark" : "light"}
                    onThemeChange={(next, variant, trigger) => applyThemeWithTransition(next, trigger, setDarkMode, variant)}
                  />
                </div>
              </div>
            </div>
            <div className="user-pill">
              <div className="online-dot" />
              {user?.name}
            </div>
            <button onClick={logout} className="btn-header">
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="dash-main">
        <div
          className="page-heading-row"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "12px",
            flexWrap: "wrap",
          }}
        >
          <div className="qa-tabs">
            {TABS.map((t) => (
              <button
                key={t.key}
                className={`qa-tab ${tab === t.key ? "active" : ""}`}
                onClick={() => setTab(t.key)}
              >
                {t.label}
                {t.key === tab && pagination.total > 0 && (
                  <span
                    style={{
                      marginLeft: "6px",
                      fontSize: "10px",
                      opacity: 0.6,
                      fontFamily: "var(--mono)",
                    }}
                  >
                    ({pagination.total})
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Search Bar */}
          <div className="search-wrap">
            <SearchField
              label="Search"
              hideLabel
              value={searchQuery}
              onValueChange={handleSearchChange}
              placeholder="Search by company name, number, or locality..."
            />
          </div>
        </div>

        {error && <div className="qa-notes-box">{error}</div>}

        <div className="table-wrap">
          {!loading && rows.length > 0 && (
            <div className="table-toolbar">
              <span className="toolbar-label">
                {pagination.total.toLocaleString()} results
              </span>
            </div>
          )}

          {loading ? (
            <div className="empty-state" style={{ width: "100%" }}>
              <Skeleton lines={6} label="Loading QA queue" />
            </div>
          ) : rows.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-label">
                {searchDebounce
                  ? `No results found for "${searchDebounce}"`
                  : "Nothing here right now"}
              </div>
            </div>
          ) : (
            <>
              <div className="qa-list">
                {rows.map((row) => {
                  const { name, id } = formatCompanyDisplay(row);
                  return (
                    <div className="qa-card" key={row.id || row._id}>
                      <div className="qa-card-info">
                        <div className="qa-card-name">
                          <span>{name}</span>
                          <span className="qa-card-company-id">#{id}</span>
                        </div>
                        <div className="qa-card-sub">
                          {row.locality ? `${row.locality} · ` : ""}
                          {row.customerName
                            ? `Customer: ${row.customerName} · `
                            : ""}
                          {row.dudaSiteName ||
                            row.duda_site_name ||
                            row.siteName ||
                            "No site name"}
                        </div>
                        {row.qaNotes && (
                          <div className="qa-notes-box">
                            Note: {row.qaNotes}
                          </div>
                        )}
                      </div>
                      <div className="qa-card-actions">
                        {(row.previewUrl || row.preview_url) && (
                          <a
                            className="btn btn-ghost btn-sm"
                            href={row.previewUrl || row.preview_url}
                            target="_blank"
                            rel="noreferrer"
                          >
                            Preview
                          </a>
                        )}
                        {tab === "pending_qa" && (
                          <>
                            <button
                              className="btn btn-danger-ghost btn-sm"
                              disabled={busyId === (row.id || row._id)}
                              onClick={() => openRegen(row)}
                            >
                              Request regeneration
                            </button>
                            <button
                              className="btn btn-success-ghost btn-sm"
                              disabled={busyId === (row.id || row._id)}
                              onClick={() => handleApprove(row.id || row._id)}
                            >
                              Approve
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Pagination - matches CustomerDashboard style */}
              {pagination.totalPages > 1 && (
                <div className="pagination">
                  <div className="page-info">
                    Page <b>{pagination.currentPage}</b> of{" "}
                    <b>{pagination.totalPages}</b>&ensp;·&ensp;
                    {pagination.total.toLocaleString()} total
                  </div>
                  <div className="page-controls">
                    <button
                      onClick={() => handlePageChange(1)}
                      disabled={pagination.currentPage === 1}
                      className="btn-page"
                      title="First"
                    >
                      «
                    </button>
                    <button
                      onClick={() =>
                        handlePageChange(pagination.currentPage - 1)
                      }
                      disabled={pagination.currentPage === 1}
                      className="btn-page"
                      title="Previous"
                    >
                      ‹
                    </button>
                    {getPageNumbers().map((page, i) =>
                      page === "..." ? (
                        <span key={`e${i}`} className="page-ellipsis">
                          …
                        </span>
                      ) : (
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          className={`btn-page${pagination.currentPage === page ? " active" : ""}`}
                        >
                          {page}
                        </button>
                      ),
                    )}
                    <button
                      onClick={() =>
                        handlePageChange(pagination.currentPage + 1)
                      }
                      disabled={
                        pagination.currentPage === pagination.totalPages
                      }
                      className="btn-page"
                      title="Next"
                    >
                      ›
                    </button>
                    <button
                      onClick={() => handlePageChange(pagination.totalPages)}
                      disabled={
                        pagination.currentPage === pagination.totalPages
                      }
                      className="btn-page"
                      title="Last"
                    >
                      »
                    </button>
                  </div>
                  <div className="page-jump">
                    Go to
                    <Input
                      label="Go to page"
                      hideLabel
                      type="number"
                      min={1}
                      max={pagination.totalPages}
                      value={jumpPage}
                      onChange={(e) => setJumpPage(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleJumpPage()}
                      className="page-jump-input"
                      placeholder="-"
                    />
                    <button
                      onClick={handleJumpPage}
                      className="btn btn-ghost btn-sm"
                    >
                      Go
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {regenTarget && (
        <Dialog
          open
          onOpenChange={(next) => {
            if (!next) setRegenTarget(null);
          }}
        >
          <DialogContent
            className="regen-modal"
            title={`What needs fixing on ${
              regenTarget.companyName ||
              regenTarget.company_name ||
              regenTarget.name ||
              regenTarget.businessName ||
              regenTarget.companyId ||
              regenTarget.company_id ||
              "this company"
            }?`}
            description="This sends the site back for rebuild with your notes attached."
          >
            <Textarea
              label="Notes"
              hideLabel
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. wrong hero image, phone number missing, copy sounds generic..."
              autoFocus
            />
            <div className="qa-modal-actions">
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => setRegenTarget(null)}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger-ghost btn-sm"
                disabled={
                  !notes.trim() ||
                  busyId === (regenTarget.id || regenTarget._id)
                }
                onClick={submitRegen}
              >
                Send back for rebuild
              </button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
